import { ReactNode, useRef, useState } from 'react';
import { Tooltip as TooltipBootstrap, Overlay } from 'react-bootstrap';
import { classNames } from 'utils/classNameUtils';

interface TooltipProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  id: string;
  text: string | ReactNode;
  placement?: 'top' | 'right' | 'bottom' | 'left';
  shouldShow?: (container: HTMLSpanElement) => boolean;
  zIndex?: number;
}

const Tooltip = ({
  children,
  className,
  delay,
  id,
  text,
  placement,
  shouldShow,
  zIndex,
}: TooltipProps) => {
  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(undefined);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tooltip = (
    <TooltipBootstrap
      id={id}
      placement={placement}
      style={zIndex ? { zIndex } : {}}
    >
      {text}
    </TooltipBootstrap>
  );

  const handleMouseEnter = (event: any) => {
    const { currentTarget } = event;

    if (shouldShow && !shouldShow(currentTarget)) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      setShow(true);
      setTarget(event.target);
    }, delay ?? 0);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setShow(false);
  };

  return (
    <span
      data-testid="tooltip-container"
      className={classNames({
        'tooltip-container': true,
        [className ?? '']: !!className,
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <Overlay target={target} show={show} placement={placement}>
        {tooltip}
      </Overlay>
    </span>
  );
};

Tooltip.defaultProps = {
  className: undefined,
  delay: undefined,
  placement: 'top',
  shouldShow: undefined,
  zIndex: undefined,
};

export default Tooltip;
